import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import productService from '../../../services/productService';
import { useCurrency } from '../../../hooks/useCurrency';

const AttributeDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currencySymbol } = useCurrency();
  const [attribute, setAttribute] = useState(null);
  const [attributeValues, setAttributeValues] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Add Value Dialog
  const [addValueDialogOpen, setAddValueDialogOpen] = useState(false);
  const [newValue, setNewValue] = useState({
    value: '',
    displayValue: '',
    priceModifier: '0',
    sortOrder: '0',
  });

  // Edit Value Dialog
  const [editValueDialogOpen, setEditValueDialogOpen] = useState(false);
  const [editingValue, setEditingValue] = useState(null);
  const [editValue, setEditValue] = useState({
    value: '',
    displayValue: '',
    priceModifier: '0',
    sortOrder: '0',
  });

  // Delete Dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [valueToDelete, setValueToDelete] = useState(null);

  useEffect(() => {
    fetchAttribute();
    fetchAttributeValues();
  }, [id]);

  const fetchAttribute = async () => {
    try {
      setLoading(true);
      const data = await productService.getAttributeById(id);
      setAttribute(data);
    } catch (error) {
      toast.error(t('attributes.fetchError'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAttributeValues = async () => {
    try {
      const data = await productService.getAttributeValues(id);
      setAttributeValues(data);
    } catch (error) {
      console.error('Failed to fetch attribute values:', error);
    }
  };

  // ============ ADD VALUE ============
  const handleAddValueOpen = () => {
    setNewValue({
      value: '',
      displayValue: '',
      priceModifier: '0',
      sortOrder: '0',
    });
    setAddValueDialogOpen(true);
  };

  const handleAddValueClose = () => {
    setAddValueDialogOpen(false);
  };

  const handleAddValueSubmit = async () => {
    try {
      const valueData = {
        attributeId: id,
        value: newValue.value,
        displayValue: newValue.displayValue || newValue.value,
        priceModifier: parseFloat(newValue.priceModifier) || 0,
        sortOrder: parseInt(newValue.sortOrder) || 0,
      };

      await productService.createAttributeValue(valueData);
      toast.success(t('attributes.createSuccess'));
      setAddValueDialogOpen(false);
      fetchAttributeValues();
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.saveError'));
      console.error(error);
    }
  };

  // ============ EDIT VALUE ============
  const handleEditValueOpen = (value) => {
    setEditingValue(value);
    setEditValue({
      value: value.value,
      displayValue: value.displayValue,
      priceModifier: value.priceModifier.toString(),
      sortOrder: value.sortOrder.toString(),
    });
    setEditValueDialogOpen(true);
  };

  const handleEditValueClose = () => {
    setEditValueDialogOpen(false);
    setEditingValue(null);
  };

  const handleEditValueSubmit = async () => {
    try {
      const valueData = {
        value: editValue.value,
        displayValue: editValue.displayValue || editValue.value,
        priceModifier: parseFloat(editValue.priceModifier) || 0,
        sortOrder: parseInt(editValue.sortOrder) || 0,
      };

      await productService.updateAttributeValue(editingValue.id, valueData);
      toast.success(t('attributes.updateSuccess'));
      setEditValueDialogOpen(false);
      setEditingValue(null);
      fetchAttributeValues();
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.saveError'));
      console.error(error);
    }
  };

  // ============ DELETE VALUE ============
  const handleDeleteClick = (value) => {
    setValueToDelete(value);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await productService.deleteAttributeValue(valueToDelete.id);
      toast.success(t('attributes.deleteSuccess'));
      setDeleteDialogOpen(false);
      setValueToDelete(null);
      fetchAttributeValues();
    } catch (error) {
      toast.error(error.response?.data?.message || t('common.deleteError'));
      console.error(error);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setValueToDelete(null);
  };

  if (loading || !attribute) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center' }}>
        <Typography>{t('common.loading')}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/attributes')}
          >
            {t('common.back')}
          </Button>
          <Typography variant="h4">{attribute.name}</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/attributes/edit/${id}`)}
        >
          {t('attributes.editAttribute')}
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Attribute Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('attributes.viewAttribute')}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t('common.name')}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {attribute.name}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t('common.type')}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {attribute.type}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t('attributes.unit')}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {attribute.unit || t('common.notAvailable')}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t('attributes_ext.required')}
                  </Typography>
                  <Chip
                    label={attribute.isRequired ? t('common.yes') : t('common.no')}
                    color={attribute.isRequired ? 'error' : 'default'}
                    size="small"
                  />
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t('common.sortOrder')}
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {attribute.sortOrder}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    {t('attributes.values')}
                  </Typography>
                  <Chip
                    label={attribute.valueCount || 0}
                    color="primary"
                    size="small"
                  />
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary">
                      {t('roles.store')}
                    </Typography>
                    <Typography variant="body1" fontWeight={600}>
                      {attribute.storeName} ({attribute.storeCode})
                    </Typography>
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary">
                    {t('common.createdAt')}
                  </Typography>
                  <Typography variant="body1">
                    {new Date(attribute.createdAt).toLocaleString()}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Attribute Values */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  {t('attributes_ext.attributeValuesCount', { count: attributeValues.length })}
                </Typography>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddValueOpen}
                >
                  {t('attributes.addValue')}
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />

              {attributeValues.length > 0 ? (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('attributes_ext.value')}</TableCell>
                        <TableCell>{t('attributes_ext.display')}</TableCell>
                        <TableCell>{t('attributes_ext.priceModifierShort')}</TableCell>
                        <TableCell>{t('attributes_ext.sort')}</TableCell>
                        <TableCell>{t('common.actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {attributeValues.map((value) => (
                        <TableRow key={value.id}>
                          <TableCell>{value.value}</TableCell>
                          <TableCell>{value.displayValue}</TableCell>
                          <TableCell>
                            {value.priceModifier > 0 ? '+' : ''}
                            {value.priceModifier} {currencySymbol}
                          </TableCell>
                          <TableCell>{value.sortOrder}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleEditValueOpen(value)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteClick(value)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary" align="center">
                  {t('common.noData')}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add Value Dialog */}
      <Dialog open={addValueDialogOpen} onClose={handleAddValueClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t('attributes.addValue')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('attributes_ext.value')}
                value={newValue.value}
                onChange={(e) => setNewValue({ ...newValue, value: e.target.value })}
                required
                placeholder={t('attributes_ext.valuePlaceholder')}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('attributes_ext.displayValue')}
                value={newValue.displayValue}
                onChange={(e) => setNewValue({ ...newValue, displayValue: e.target.value })}
                placeholder={t('attributes_ext.displayValuePlaceholder')}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label={`${t('attributes_ext.priceModifier')} (${currencySymbol})`}
                type="number"
                value={newValue.priceModifier}
                onChange={(e) => setNewValue({ ...newValue, priceModifier: e.target.value })}
                inputProps={{ step: 0.01 }}
                helperText={t('attributes_ext.priceModifierHelper')}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label={t('common.sortOrder')}
                type="number"
                value={newValue.sortOrder}
                onChange={(e) => setNewValue({ ...newValue, sortOrder: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAddValueClose}>{t('common.cancel')}</Button>
          <Button onClick={handleAddValueSubmit} variant="contained">
            {t('attributes.addValue')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Value Dialog */}
      <Dialog open={editValueDialogOpen} onClose={handleEditValueClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t('common.edit') + ' ' + t('attributes.valueName')}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('attributes_ext.value')}
                value={editValue.value}
                onChange={(e) => setEditValue({ ...editValue, value: e.target.value })}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('attributes_ext.displayValue')}
                value={editValue.displayValue}
                onChange={(e) => setEditValue({ ...editValue, displayValue: e.target.value })}
                placeholder={t('common.leaveEmptyToUseValue')}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label={`${t('attributes_ext.priceModifier')} (${currencySymbol})`}
                type="number"
                value={editValue.priceModifier}
                onChange={(e) => setEditValue({ ...editValue, priceModifier: e.target.value })}
                inputProps={{ step: 0.01 }}
                helperText={t('attributes_ext.priceModifierHelper')}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label={t('common.sortOrder')}
                type="number"
                value={editValue.sortOrder}
                onChange={(e) => setEditValue({ ...editValue, sortOrder: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditValueClose}>{t('common.cancel')}</Button>
          <Button onClick={handleEditValueSubmit} variant="contained">
            {t('common.update')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>{t('attributes.deleteAttribute')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('common.deleteConfirmText', { name: valueToDelete?.displayValue })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>{t('common.cancel')}</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AttributeDetails;